/**
 * Per-user Control Center dashboard layout (US 34842): each section is shown,
 * collapsed (header only) or hidden, plus an optional rotate mode that
 * spotlights one section at a time. Pure; the web shell persists the prefs in
 * localStorage per signed-in account and runs the rotation clock.
 *
 * localStorage, not the bridge: the bridge's FlexData path is authed with the
 * shared seat JWT, so it cannot tell which person is viewing, and a layout is
 * per screen anyway (the TV rotates, a desk does not).
 */

export const DASHBOARD_SECTIONS = [
  'tiles',
  'alerts',
  'resetSoon',
  'accounts',
  'needsYou',
  'inbound',
  'machines',
] as const;
export type DashboardSection = (typeof DASHBOARD_SECTIONS)[number];

export const SECTION_LABELS: Record<DashboardSection, string> = {
  tiles: 'Problem tiles',
  alerts: 'Needs attention',
  resetSoon: 'Resetting in 72h',
  accounts: 'Accounts',
  needsYou: 'Needs you',
  inbound: 'Inbound queue',
  machines: 'Machines',
};

/** The filter strip sits above the grid; rotation spotlights grid cards only. */
export const ROTATING_SECTIONS: readonly DashboardSection[] =
  DASHBOARD_SECTIONS.filter((s) => s !== 'tiles');

export const SECTION_MODES = ['shown', 'collapsed', 'hidden'] as const;
export type SectionMode = (typeof SECTION_MODES)[number];

export const ROTATE_DEFAULT_SECONDS = 25;
export const ROTATE_MIN_SECONDS = 5;
export const ROTATE_MAX_SECONDS = 600;

export interface SectionLayout {
  /** Sections absent from the map are shown. */
  modes: Partial<Record<DashboardSection, SectionMode>>;
  rotate: boolean;
  rotateSeconds: number;
}

export const DEFAULT_SECTION_LAYOUT: SectionLayout = {
  modes: {},
  rotate: false,
  rotateSeconds: ROTATE_DEFAULT_SECONDS,
};

/** v1 in the key so a future shape change starts clean instead of misreading. */
export function sectionLayoutStorageKey(accountId: string | null): string {
  return `roost.dashboard.layout.v1:${accountId || 'anonymous'}`;
}

export function clampRotateSeconds(value: unknown): number {
  // Only a number or a non-blank string counts: Number(null), Number('') and
  // Number(false) are 0, which would clamp to the 5 s floor (PR 4368 review).
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (!Number.isFinite(n)) return ROTATE_DEFAULT_SECONDS;
  return Math.min(
    ROTATE_MAX_SECONDS,
    Math.max(ROTATE_MIN_SECONDS, Math.round(n))
  );
}

function isSection(v: string): v is DashboardSection {
  return (DASHBOARD_SECTIONS as readonly string[]).includes(v);
}

function isMode(v: unknown): v is SectionMode {
  return (SECTION_MODES as readonly unknown[]).includes(v);
}

/**
 * Reads a stored layout. Anything missing, unknown or garbled falls back to
 * the default for that field, never an error: a bad value must not blank the
 * wallboard.
 */
export function parseSectionLayout(raw: string | null): SectionLayout {
  if (!raw) return DEFAULT_SECTION_LAYOUT;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return DEFAULT_SECTION_LAYOUT;
  }
  if (!data || typeof data !== 'object') return DEFAULT_SECTION_LAYOUT;
  const obj = data as Record<string, unknown>;
  const modes: Partial<Record<DashboardSection, SectionMode>> = {};
  if (obj['modes'] && typeof obj['modes'] === 'object') {
    for (const [k, v] of Object.entries(obj['modes'] as object)) {
      if (isSection(k) && isMode(v) && v !== 'shown') modes[k] = v;
    }
  }
  return {
    modes,
    rotate: obj['rotate'] === true,
    rotateSeconds:
      obj['rotateSeconds'] === undefined
        ? ROTATE_DEFAULT_SECONDS
        : clampRotateSeconds(obj['rotateSeconds']),
  };
}

export function sectionMode(
  layout: SectionLayout,
  section: DashboardSection
): SectionMode {
  return layout.modes[section] ?? 'shown';
}

export function withSectionMode(
  layout: SectionLayout,
  section: DashboardSection,
  mode: SectionMode
): SectionLayout {
  const modes = { ...layout.modes };
  if (mode === 'shown') delete modes[section];
  else modes[section] = mode;
  return { ...layout, modes };
}

/** The card-header chevron: shown <-> collapsed. A hidden card has no header. */
export function withSectionCollapseToggled(
  layout: SectionLayout,
  section: DashboardSection
): SectionLayout {
  const next =
    sectionMode(layout, section) === 'collapsed' ? 'shown' : 'collapsed';
  return withSectionMode(layout, section, next);
}

/** Grid cards rotation visits, in page order; hidden ones are skipped. */
export function rotationOrder(layout: SectionLayout): DashboardSection[] {
  return ROTATING_SECTIONS.filter((s) => sectionMode(layout, s) !== 'hidden');
}

export interface RotationState {
  current: DashboardSection | null;
  /** When the spotlight moves on, unless paused. */
  dueAtMs: number;
}

export interface RotationInput {
  order: readonly DashboardSection[];
  nowMs: number;
  intervalMs: number;
  /** Pointer over the grid, the Layout strip open, or a recent interaction. */
  paused: boolean;
}

/**
 * One clock tick. A pause restarts the full interval rather than resuming a
 * partial one, so the section the viewer was reading never flips the instant
 * they look away. A spotlight whose section was hidden jumps to the first
 * visible one.
 */
export function rotationTick(
  state: RotationState,
  { order, nowMs, intervalMs, paused }: RotationInput
): RotationState {
  if (order.length === 0) return { current: null, dueAtMs: nowMs + intervalMs };
  const at = state.current === null ? -1 : order.indexOf(state.current);
  if (at < 0) return { current: order[0], dueAtMs: nowMs + intervalMs };
  if (paused) return { current: state.current, dueAtMs: nowMs + intervalMs };
  if (nowMs < state.dueAtMs) return state;
  return {
    current: order[(at + 1) % order.length],
    dueAtMs: nowMs + intervalMs,
  };
}

export interface SectionView {
  /** Not rendered at all: hidden, or off-spotlight while rotating. */
  off: boolean;
  /** Header only. Ignored for the spotlit section, which shows in full. */
  collapsed: boolean;
}

/** What each section renders as, one lookup for the template. */
export function sectionViews(
  layout: SectionLayout,
  spotlight: DashboardSection | null
): Record<DashboardSection, SectionView> {
  const rotating = layout.rotate && spotlight !== null;
  const out = {} as Record<DashboardSection, SectionView>;
  for (const s of DASHBOARD_SECTIONS) {
    const mode = sectionMode(layout, s);
    const offSpot = rotating && s !== 'tiles' && s !== spotlight;
    out[s] = {
      off: mode === 'hidden' || offSpot,
      collapsed: mode === 'collapsed' && !(rotating && s === spotlight),
    };
  }
  return out;
}
